import React, { useState, useMemo } from 'react';
import { signInWithPopup, sendSignInLinkToEmail } from 'firebase/auth';
import { auth, googleProvider, isFirebaseConfigured } from '../src/firebaseConfig';
import { XIcon } from './icons/XIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { GlobeIcon } from './icons/GlobeIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { ExternalLinkIcon } from './icons/ExternalLinkIcon';

interface LoginModalProps {
    onClose: () => void;
    onLoginSuccess: (user: any) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onClose, onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<React.ReactNode | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [linkSent, setLinkSent] = useState(false);
    const [copied, setCopied] = useState(false);

    const currentHostname = useMemo(() => {
        try {
            return window.location.hostname || 'localhost';
        } catch (e) {
            return 'localhost';
        }
    }, []);

    const copyDomain = () => {
        navigator.clipboard.writeText(currentHostname);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const renderUnauthorizedError = (specificCode: string) => (
        <div className="text-left bg-orange-50 border-2 border-orange-200 p-5 rounded-2xl animate-fade-in shadow-inner mt-4">
            <div className="flex items-center gap-2 mb-3 text-orange-700">
                <ShieldIcon className="w-5 h-5" />
                <p className="font-black uppercase text-[10px] tracking-widest">Configuration Required</p>
            </div>
            
            {specificCode === 'auth/invalid-api-key' ? (
                <p className="text-[11px] text-orange-900 mb-4 font-bold leading-tight">
                    The Firebase API Key is invalid or missing. Please check your environment variables in Vercel/Hosting.
                </p>
            ) : (
                <>
                    <p className="text-[11px] text-orange-900 mb-4 font-bold leading-tight">
                        Firebase rejected <strong>{currentHostname}</strong>. You must authorize this domain to enable login.
                    </p>
                    <div className="space-y-4">
                        <div className="p-3 bg-white rounded-xl border border-orange-200">
                            <p className="text-[9px] text-orange-400 uppercase font-black mb-1">Copy this exact string:</p>
                            <div className="flex items-center justify-between gap-2">
                                <code className="text-xs font-black text-orange-900 break-all">{currentHostname}</code>
                                <button 
                                    onClick={copyDomain}
                                    className="flex-shrink-0 bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-orange-700"
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                        </div>
                        <p className="text-[10px] text-orange-800 font-bold leading-tight">
                            Go to <strong>Authentication &gt; Settings &gt; Authorized Domains</strong> in Firebase and add the value above.
                        </p>
                        <a 
                            href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/settings" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg shadow-orange-200"
                        >
                            Fix in Firebase Console
                            <ExternalLinkIcon className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </>
            )}
        </div>
    );

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (isFirebaseConfigured && auth) {
                const result = await signInWithPopup(auth, googleProvider);
                onLoginSuccess(result.user);
            } else {
                setError(renderUnauthorizedError('auth/invalid-api-key'));
            }
        } catch (err: any) {
            console.error("Google Login Error:", err);
            if (err.code === 'auth/unauthorized-domain' || err.code === 'auth/unauthorized-continue-uri') {
                setError(
                    <div className="text-left bg-orange-50 border-2 border-orange-200 p-5 rounded-2xl animate-fade-in shadow-inner mt-4">
                        <div className="flex items-center gap-2 mb-3 text-orange-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-[10px] tracking-widest">Domain Not Authorized</p>
                        </div>
                        <p className="text-[11px] text-orange-900 mb-4 font-bold leading-tight">
                            Firebase rejected the domain: <strong className="text-red-600">{currentHostname}</strong>. 
                        </p>
                        <div className="space-y-4">
                            <div className="p-3 bg-white rounded-xl border border-orange-200">
                                <p className="text-[9px] text-orange-400 uppercase font-black mb-1">Copy this and add to Firebase:</p>
                                <div className="flex items-center justify-between gap-2">
                                    <code className="text-xs font-black text-orange-900 break-all">{currentHostname}</code>
                                    <button 
                                        onClick={copyDomain}
                                        className="flex-shrink-0 bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-orange-700"
                                    >
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                            <a 
                                href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/settings" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg shadow-orange-200"
                            >
                                Open Firebase Settings
                                <ExternalLinkIcon className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                );
            } else if (err.code === 'auth/invalid-api-key') {
                setError(renderUnauthorizedError(err.code));
            } else if (err.code === 'auth/popup-closed-by-user') {
                setError(
                    <div className="text-left bg-blue-50 border border-blue-100 p-4 rounded-xl mt-4">
                        <p className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-1">Sign-In Interrupted</p>
                        <p className="text-blue-800 text-[11px] font-medium leading-tight">
                            The login window was closed before finishing. Please try again and make sure to complete the Google sign-in process.
                        </p>
                    </div>
                );
            } else {
                setError(
                    <div className="text-left bg-red-50 border border-red-100 p-4 rounded-xl mt-4">
                        <p className="text-red-600 font-bold text-xs uppercase tracking-widest mb-1">Login Error</p>
                        <p className="text-red-800 text-[11px] font-medium leading-tight">{err.message}</p>
                    </div>
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleMagicLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            if (isFirebaseConfigured && auth) {
                // Determine the best redirect URL. 
                // For the AI Studio preview, window.location.origin is often a dynamic proxy URL.
                const redirectUrl = window.location.origin + '/?email_link=true';
                
                const actionCodeSettings = {
                    url: redirectUrl,
                    handleCodeInApp: true,
                    // If the user has a mobile app, they can configure these in Firebase Console
                    // and we could optionally pass them here:
                    // dynamicLinkDomain: 'forgivepath.page.link', // Deprecated in favor of Hosting domains
                };

                console.log("[AUTH] Sending magic link. Redirect:", redirectUrl);
                
                await sendSignInLinkToEmail(auth, email, actionCodeSettings);
                
                window.localStorage.setItem('emailForSignIn', email);
                setLinkSent(true);
            } else {
                setError(
                    <div className="text-left bg-amber-50 border-2 border-amber-200 p-5 rounded-2xl animate-fade-in shadow-inner mt-4">
                        <div className="flex items-center gap-2 mb-3 text-amber-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-[10px] tracking-widest">Firebase Not Ready</p>
                        </div>
                        <p className="text-[11px] text-amber-900 mb-4 font-bold leading-tight">
                            Firebase API keys are missing or invalid. Magic links cannot be sent.
                        </p>
                    </div>
                );
            }
        } catch (err: any) {
            console.error("Magic Link Error:", err);
            // Handle specific error for passwordless not enabled
            if (err.code === 'auth/operation-not-allowed') {
                setError(
                    <div className="text-left bg-red-50 border-2 border-red-200 p-5 rounded-2xl animate-fade-in shadow-inner mt-4">
                        <div className="flex items-center gap-2 mb-3 text-red-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-[10px] tracking-widest">Auth Method Disabled</p>
                        </div>
                        <p className="text-[11px] text-red-900 mb-4 font-bold leading-tight">
                            <strong>Email Link (Passwordless)</strong> is not enabled in your Firebase Console.
                        </p>
                        <p className="text-[10px] text-red-800 font-medium">1. Go to Authentication &gt; Sign-in method.</p>
                        <p className="text-[10px] text-red-800 font-medium">2. Enable Email/Password AND toggle "Email link" ON.</p>
                    </div>
                );
            } else if (err.code === 'auth/unauthorized-domain' || err.code === 'auth/unauthorized-continue-uri') {
                setError(
                    <div className="text-left bg-orange-50 border-2 border-orange-200 p-5 rounded-2xl animate-fade-in shadow-inner mt-4">
                        <div className="flex items-center gap-2 mb-3 text-orange-700">
                            <ShieldIcon className="w-5 h-5" />
                            <p className="font-black uppercase text-[10px] tracking-widest">Domain Not Authorized</p>
                        </div>
                        <p className="text-[11px] text-orange-900 mb-4 font-bold leading-tight">
                            Firebase rejected: <strong className="text-red-600">{currentHostname}</strong>.
                        </p>
                        <div className="space-y-4">
                            <div className="p-3 bg-white rounded-xl border border-orange-200">
                                <p className="text-[9px] text-orange-400 uppercase font-black mb-1">Add this to Authorized Domains:</p>
                                <div className="flex items-center justify-between gap-2">
                                    <code className="text-xs font-black text-orange-900 break-all">{currentHostname}</code>
                                    <button 
                                        onClick={copyDomain}
                                        className="flex-shrink-0 bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-orange-700"
                                    >
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                            <a 
                                href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/settings" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-black py-3 rounded-xl transition-all flex items-center justify-center gap-2 uppercase tracking-widest shadow-lg shadow-orange-200"
                            >
                                Fix Domain in Firebase
                                <ExternalLinkIcon className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                );
            } else {
                setError(<p className="text-red-600 font-bold text-xs p-3 bg-red-50 rounded-lg mt-4">{err.message}</p>);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (linkSent) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-10 text-center relative">
                    <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                    <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <SparkleIcon className="w-10 h-10 animate-pulse" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-tighter">Check Your Email</h2>
                    <p className="text-gray-600 mb-6 font-medium">
                        We sent a magic sign-in link to <strong className="text-blue-600">{email}</strong>. 
                        Click the link in your inbox to securely access your account.
                    </p>
                    
                    <div className="bg-amber-50 rounded-2xl p-4 mb-8 border border-amber-100 text-left">
                        <p className="text-[10px] text-amber-700 font-black uppercase tracking-widest mb-2 flex items-center gap-2">
                             Still no email?
                        </p>
                        <div className="space-y-1 text-[10px] text-amber-900 font-medium leading-tight">
                            <p>• Check your <strong>Spam</strong> or <strong>Promotions</strong> folders.</p>
                            <p>• It can take 2-3 minutes during peak hours.</p>
                            <p>• Ensure "forgivepath-8a2ed.firebaseapp.com" is not blocked.</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button 
                            onClick={handleMagicLink} 
                            disabled={isLoading}
                            className="w-full bg-white border-2 border-blue-600 text-blue-600 font-black py-4 rounded-2xl shadow-sm hover:bg-blue-50 transition-all uppercase tracking-widest text-xs"
                        >
                            {isLoading ? <SpinnerIcon className="w-5 h-5 animate-spin mx-auto" /> : "Resend Magic Link"}
                        </button>
                        <button onClick={onClose} className="w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg uppercase tracking-widest text-xs">Got it, I'll check my mail</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-10 relative overflow-hidden">
                <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 z-10"><XIcon className="w-6 h-6" /></button>
                
                <div className="text-center mb-8 relative z-10">
                    <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tighter">Log In</h2>
                    <p className="text-gray-500 font-medium mt-1">Access your forgiveness dashboard.</p>
                </div>

                <div className="space-y-6 relative z-10">
                    {window.self !== window.top && (
                        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-3xl shadow-xl mb-6 text-white animate-pulse-slow">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <GlobeIcon className="w-5 h-5 text-white" />
                                </div>
                                <p className="font-black uppercase text-xs tracking-widest">Action Required</p>
                            </div>
                            <p className="text-sm font-bold leading-tight mb-4">
                                Logins are blocked inside this preview window. You must open the app in a full tab to sign in.
                            </p>
                            <button 
                                onClick={() => window.open(window.location.href, '_blank')}
                                className="w-full bg-white text-blue-600 font-black py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all uppercase tracking-widest text-xs"
                            >
                                Open in New Tab & Login
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleMagicLink} className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Secure Email Access</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold transition-all text-sm"
                                placeholder="Email address"
                                required
                            />
                        </div>
                        <button type="submit" disabled={isLoading} className="w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2 disabled:bg-gray-300">
                            {isLoading ? <SpinnerIcon className="w-5 h-5 animate-spin" /> : "Sign In with Email"}
                        </button>
                    </form>

                    <div className="relative flex items-center py-2">
                        <div className="flex-grow border-t border-gray-100"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-black uppercase tracking-widest">or continue with</span>
                        <div className="flex-grow border-t border-gray-100"></div>
                    </div>

                    <button 
                        onClick={handleGoogleLogin} 
                        disabled={isLoading} 
                        className="w-full flex items-center justify-center gap-3 bg-gray-50 border-2 border-gray-100 hover:bg-white hover:border-gray-300 text-gray-500 font-bold py-3 rounded-2xl transition-all disabled:opacity-50 text-xs"
                    >
                        <GlobeIcon className="w-5 h-5 grayscale opacity-70" />
                        Sign in with Google
                    </button>
                    
                    {error && (
                        <div className="mt-2">
                            {error}
                        </div>
                    )}

                    <div className="mt-6 pt-6 border-t border-gray-100">
                        <div className="bg-gray-50 rounded-2xl p-4 mb-4 border border-gray-100">
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3">Still no email? (Checklist)</p>
                            <div className="space-y-3">
                                <div className="text-[10px] text-gray-600 font-medium space-y-1">
                                    <p>1. <strong>Enable Provider:</strong> In Firebase Console &gt; Authentication &gt; Sign-in method, ensure <strong>Email/Password</strong> is enabled AND the <strong>Email Link</strong> toggle is ON.</p>
                                    <p>2. <strong>Check Templates:</strong> In the Templates tab, you MUST see "Email link (passwordless sign-in)". If it's missing, step 1 was skipped.</p>
                                    <p>3. <strong>Authorize Domain:</strong> Add your current domain to the Authorized Domains list.</p>
                                </div>
                                <button 
                                    onClick={copyDomain}
                                    className="w-full flex items-center justify-between bg-white p-3 rounded-xl border border-gray-200 hover:border-blue-300 transition-all group"
                                >
                                    <span className="text-[10px] font-bold text-gray-600">Copy Domain to Authorize</span>
                                    <span className="text-[9px] font-black text-blue-600 uppercase group-hover:underline">{copied ? 'Copied!' : 'Copy'}</span>
                                </button>
                                <a 
                                    href="https://console.firebase.google.com/project/forgivepath-8a2ed/authentication/settings"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-between bg-white p-3 rounded-xl border border-gray-200 hover:border-blue-300 transition-all"
                                >
                                    <span className="text-[10px] font-bold text-gray-600">Open Firebase Console</span>
                                    <ExternalLinkIcon className="w-3 h-3 text-gray-400" />
                                </a>
                            </div>
                        </div>

                        <button 
                            onClick={async () => {
                                if (!email) {
                                    setError("Enter your email first to test.");
                                    return;
                                }
                                setIsLoading(true);
                                const { sendTestEmail, getEmailConfig } = await import('../services/emailService');
                                const config = getEmailConfig();
                                const result = await sendTestEmail(email);
                                setIsLoading(false);
                                if (result.success) {
                                    setError(
                                        <div className="text-left bg-green-50 border border-green-100 p-4 rounded-xl mt-4">
                                            <p className="text-green-600 font-bold text-[10px] uppercase tracking-widest mb-1">✓ Test Signal Sent</p>
                                            <p className="text-green-800 text-[11px] font-medium leading-tight">
                                                EmailJS reported success. If you don't see it in 1 minute, check <strong>Spam</strong> and <strong>Promotions</strong>.
                                            </p>
                                            <p className="text-[9px] text-green-600 mt-2 font-mono">Service: {config.serviceId.substring(0, 8)}...</p>
                                        </div>
                                    );
                                } else {
                                    setError(
                                        <div className="text-left bg-red-50 border border-red-100 p-4 rounded-xl mt-4">
                                            <p className="text-red-600 font-bold text-[10px] uppercase tracking-widest mb-1">✗ Connection Failed</p>
                                            <p className="text-red-800 text-[11px] font-medium leading-tight">
                                                Error: {result.error}
                                            </p>
                                            <p className="text-[9px] text-red-600 mt-2 font-bold">
                                                This means your EmailJS keys are likely missing or incorrect in your settings.
                                            </p>
                                        </div>
                                    );
                                }
                            }}
                            className="w-full text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline"
                        >
                            Send a Diagnostic Test Email
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;